import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';
import { ToastrHelper } from '../../../../helpers/toastr.helper';
import { Community } from '../../../../models/identity/community.model';
import { Organization } from '../../../../models/identity/organization.model';
import { UserType } from '../../../../models/identity/user-type.model';
import { AccountsService } from '../../../../services/identity/accounts.service';
import { OrganizationService } from '../../../../services/identity/organization.service';
import { UserService } from '../../../../services/identity/user.service';
import { InviteMemberCommand, InviteMemberUseCase } from './invite-member.usecase';

@Component({
    selector: 'app-invite-member',
    templateUrl: './invite-member.component.html',
    styleUrls: ['./invite-member.component.scss'],
})
export class InviteMemberComponent implements OnInit {
    public availableCommunities: Community[];
    public availableOrganizations: Organization[];
    public availableRoles: UserType[];
    public isReadOnlyMode: boolean;
    public userForm: FormGroup;

    @Input() isModal: boolean = false;
    @Output() closeModalAndRefresh = new EventEmitter<boolean>();

    constructor(
        private readonly router: Router,
        private readonly organizationService: OrganizationService,
        private readonly fb: FormBuilder,
        protected readonly accountsService: AccountsService,
        private readonly toastrHelper: ToastrHelper,
        private readonly inviteMemberUseCase: InviteMemberUseCase,
        private readonly userService: UserService,
        private readonly translate: TranslateService,
    ) {
        this.availableCommunities = this.accountsService.getCommunitiesManagedByUser();
    }

    public ngOnInit(): void {
        this.initForm();
        this.setupFormListeners();
    }

    private initForm(): void {
        this.userForm = this.fb.group({
            email: this.fb.control('', [Validators.required, Validators.email]),
            firstName: this.fb.control('', [Validators.required, Validators.maxLength(50)]),
            lastName: this.fb.control('', [Validators.required, Validators.maxLength(50)]),
            organizationFromInput: this.fb.control(null, [Validators.required]),
            isOrganizationManager: this.fb.control(false),
            communitiesIds: this.fb.control([], [Validators.required]),
            primaryCommunityId: this.fb.control(null, [Validators.required]),
        });

        if (this.availableCommunities.length === 1) {
            this.userForm.patchValue({
                communitiesIds: [this.availableCommunities[0].id],
                primaryCommunityId: this.availableCommunities[0].id,
            });
        }
    }

    private setupFormListeners(): void {
        this.userForm.controls.communitiesIds.valueChanges.subscribe((communitiesIds: string[] = []) => {
            const primaryCommunityIdControl = this.userForm.controls.primaryCommunityId;
            if (!communitiesIds.includes(primaryCommunityIdControl.value)) {
                primaryCommunityIdControl.reset();
                primaryCommunityIdControl.markAsDirty();
                primaryCommunityIdControl.markAsTouched();
            }
        });
    }

    public get selectedCommunities(): Community[] {
        return this.availableCommunities?.filter((community) => this.userForm.controls.communitiesIds.value.includes(community.id)) ?? [];
    }

    public filterOrganizations(event: any) {
        this.organizationService.getOrganizationByName(event.query).subscribe(
            (organisations) => (this.availableOrganizations = organisations),
            () => this.toastrHelper.showError(this.translate.instant('shared.organizations.get.error')),
        );
    }

    public getCommunityNameById(communityId: string): string {
        return this.availableCommunities.find((community) => community.id === communityId)?.displayName || '';
    }

    public isFieldValid(field: string): boolean {
        const control = this.userForm.get(field);
        return control ? !control.valid && control.touched : false;
    }

    public onEmailBlur(): void {
        const emailControl = this.userForm.get('email');
        if (!emailControl || !emailControl.valid) {
            return;
        }

        const email = emailControl.value.toLowerCase();

        this.userService.getUserByEmail(email).subscribe((user) => this.handleUserLookupSuccess(user));
    }

    private handleUserLookupSuccess(user: any): void {
        if (!user || !user.userId) {
            return;
        }

        this.patchUserFormWithUserData(user);
        this.fetchAndPatchOrganization(user.organizationName);
        this.isReadOnlyMode = true;
    }

    private patchUserFormWithUserData(user: any): void {
        this.userForm.patchValue({
            firstName: user.firstName,
            lastName: user.lastName,
            isOrganizationManager: user.isOrganizationManager,
        });
        // Remove required validator for primaryCommunityId since the field will be hidden
        this.userForm.get('primaryCommunityId')?.clearValidators();
        this.userForm.get('primaryCommunityId')?.updateValueAndValidity();
    }

    private fetchAndPatchOrganization(organizationName: string): void {
        this.organizationService.getOrganizationByName(organizationName).subscribe(
            (organizations) => {
                if (organizations && organizations.length > 0) {
                    this.userForm.patchValue({
                        organizationFromInput: organizations[0],
                    });
                }
            },
            (error) => {
                this.toastrHelper.showError(this.translate.instant('shared.organizations.get.error'));
            },
        );
    }

    public onSubmit(): void {
        if (!this.userForm.valid) {
            this.userForm.markAllAsTouched();
            return;
        }

        const formData = this.userForm.getRawValue();

        const command: InviteMemberCommand = {
            email: formData.email,
            firstName: formData.firstName,
            lastName: formData.lastName,
            organizationInput: formData.organizationFromInput,
            isOrganizationManager: formData.isOrganizationManager,
            communitiesIds: formData.communitiesIds,
            primaryCommunityId: formData.primaryCommunityId,
        };

        this.inviteMemberUseCase.execute(command).subscribe(
            () => {
                this.toastrHelper.showSuccess(this.translate.instant('community.nav.members.invite.success'));
                if (this.isModal) {
                    this.closeModalAndRefresh.emit(true);
                } else {
                    this.router.navigate(['/community/members']);
                }
            },
            () => {
                this.toastrHelper.showError(this.translate.instant('community.nav.members.invite.error'));
            },
        );
    }

    public onCancel(): void {
        if (this.isModal) {
            this.closeModalAndRefresh.emit(false);
        } else {
            this.router.navigate(['/community/members']);
        }
    }
}
